import * as React from 'react';
import { FunctionComponent } from 'react';
import { ActionItem, ActionsButton } from '../ActionsButton/ActionsButton';
import { Pagination2 } from '../Pagination/Pagination2';
import { SelectAllCheckbox } from '../SelectAllCheckbox/SelectAllCheckbox';
import { FilterIcon } from '../FilterIcon/FilterIcon';
import { Button } from '../Button/Button';
import { PdfIcon } from '../PdfIcon/PdfIcon';
import { CsvIcon } from '../CsvIcon/CsvIcon';
import { ColumnDefinition } from '../../helpers/table/table';
import { SelectOption } from '../../helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE } from '../../consts/table';
import { DatePicker as SitDatePicker } from '../DatePicker/DatePicker';
import { TimePicker } from '../TimePicker/TimePicker';
import moment from 'moment';
import { SearchMultiSelectFilter } from '../SearchMultiSelectFilter/SearchMultiSelectFilter';
import { FilterChipSelect } from '../FilterChipSelect/FilterChipSelect';

interface Props {
  actionItems: ActionItem[];
  currentPage: number;
  setCurrentPageParams: (currentPage: number) => void;
  total: number;
  onSelectAllClick: () => void;
  onSelectAllOnPageClick: () => void;
  onUnselectAllClick: () => void;
  isSelectAllChecked: boolean;
  selectedItems: any[];
  selectedItemsCount?: number;
  onTableFilterClick: (event) => void;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  columns: ColumnDefinition[];
  allColumns?: ColumnDefinition[];
  hiddenColumns?: string[];
  fixedColumnFields?: string[];
  onToggleColumn?: (field: string) => void;
  onResetColumns?: () => void;
  filters: any;
  options?: { [field: string]: SelectOption[] };
  onClearFilterClick?: () => void;
  onClearIndividualFilter?: (field: string) => void;
  onApplyFilterClick?: () => void;
  onCancelFilterClick?: () => void;
  onTableFilterChange?: (event, value: string, options?) => void;
  renderAdditionalOptions?: () => React.ReactNode;
  goBack?: () => void;
  goBackLabel?: string;
  gridTitle?: string;
  gridTitleContent?: React.ReactNode;
  gridEyebrow?: string;
  gridSubtitle?: string;
  gridTitleCount?: number;
  toolbarSearchField?: string;
  toolbarSearchPlaceholder?: string;
  onToolbarSearchClear?: () => void;
  isActionsInline?: boolean;
  selectionActionsBelow?: boolean;
  showClearSelection?: boolean;
  hiddenFilterFields?: string[];
  isPDF?: boolean;
  isCSV?: boolean;
  isSelectDisabled: boolean;
}

export const GridPanel: FunctionComponent<Props> = ({
  actionItems,
  currentPage,
  setCurrentPageParams,
  total,
  onSelectAllClick,
  onSelectAllOnPageClick,
  onUnselectAllClick,
  isSelectAllChecked,
  selectedItems,
  selectedItemsCount,
  onTableFilterClick,
  isShowFilter,
  isDataFiltered,
  renderAdditionalOptions,
  goBack,
  goBackLabel = 'School leagues',
  gridTitle,
  gridTitleContent,
  gridEyebrow,
  gridSubtitle,
  gridTitleCount,
  toolbarSearchField,
  toolbarSearchPlaceholder,
  onToolbarSearchClear,
  isActionsInline,
  selectionActionsBelow,
  showClearSelection,
  hiddenFilterFields = [],
  isPDF,
  isCSV,
  columns,
  allColumns,
  hiddenColumns = [],
  fixedColumnFields = [],
  onToggleColumn,
  onResetColumns,
  filters,
  options = {},
  onClearFilterClick,
  onClearIndividualFilter,
  onApplyFilterClick,
  onCancelFilterClick,
  onTableFilterChange,
  isSelectDisabled
}) => {
  const [isColumnsOpen, setIsColumnsOpen] = React.useState(false);
  const columnsRef = React.useRef<HTMLDivElement>(null);
  const filtersRef = React.useRef<HTMLDivElement>(null);
  const appliedToolbarSearch = toolbarSearchField ? String(filters[toolbarSearchField] || '') : '';
  const [toolbarSearchDraft, setToolbarSearchDraft] = React.useState(appliedToolbarSearch);
  React.useEffect(() => {
    setToolbarSearchDraft(appliedToolbarSearch);
  }, [appliedToolbarSearch, toolbarSearchField]);
  const closeFilters = React.useCallback(() => {
    if (onCancelFilterClick) {
      onCancelFilterClick();
      return;
    }

    onTableFilterClick({ preventDefault: () => undefined });
  }, [onCancelFilterClick, onTableFilterClick]);
  const hideFilters = React.useCallback(() => {
    onTableFilterClick({ preventDefault: () => undefined });
  }, [onTableFilterClick]);
  const hideFiltersAfterStateUpdate = React.useCallback(() => {
    window.setTimeout(() => {
      if (filtersRef.current?.querySelector('.gridModernFilterPopover')) hideFilters();
    }, 0);
  }, [hideFilters]);
  const applyFilters = React.useCallback(() => {
    onApplyFilterClick();
    hideFiltersAfterStateUpdate();
  }, [hideFiltersAfterStateUpdate, onApplyFilterClick]);
  const clearFilters = React.useCallback(() => {
    onClearFilterClick();
    hideFiltersAfterStateUpdate();
  }, [hideFiltersAfterStateUpdate, onClearFilterClick]);
  React.useEffect(() => {
    if (!isColumnsOpen) return undefined;

    const onOutsideClick = (event: MouseEvent) => {
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
        setIsColumnsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsColumnsOpen(false);
    };

    document.addEventListener('mousedown', onOutsideClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isColumnsOpen]);
  React.useEffect(() => {
    if (!isShowFilter) return undefined;
    const onOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isDatePickerInteraction =
        target.closest('.sit-dp__pop') || target.closest('.sit-cal') || target.closest('[role="dialog"]');
      if (filtersRef.current && !filtersRef.current.contains(target) && !isDatePickerInteraction) {
        closeFilters();
      }
    };
    const onFilterKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeFilters();
    };
    document.addEventListener('mousedown', onOutsideClick);
    document.addEventListener('keydown', onFilterKeyDown);
    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
      document.removeEventListener('keydown', onFilterKeyDown);
    };
  }, [isShowFilter, closeFilters]);
  const isShowFilters = columns.some(
    column =>
      column.type !== FILTER_TYPE.NONE &&
      !hiddenFilterFields.includes(column.field) &&
      column.field !== toolbarSearchField
  );
  const activeFilters = columns.filter(column => {
    if (
      column.type === FILTER_TYPE.NONE ||
      hiddenFilterFields.includes(column.field) ||
      column.field === toolbarSearchField
    ) {
      return false;
    }
    const value = filters[column.field];
    return Array.isArray(value) ? value.length > 0 : value && (typeof value !== 'object' || value.from || value.to);
  });
  const isToolbarSearchActive = Boolean(toolbarSearchField && filters[toolbarSearchField]);
  const isRenderAdditionalOptionsExist = typeof renderAdditionalOptions !== 'undefined';
  const getActiveFilterLabel = (column: ColumnDefinition): string => {
    const value = filters[column.field];
    const columnOptions = options[column.field] || [];
    const getOptionLabel = optionValue => {
      const option = columnOptions.find(candidate => String(candidate.value) === String(optionValue));
      return option ? String(option.text) : String(optionValue);
    };

    if (Array.isArray(value)) return value.map(getOptionLabel).join(', ');
    if (value && typeof value === 'object') return [value.from, value.to].filter(Boolean).join(' – ');
    return getOptionLabel(value);
  };

  return (
    <div className={isActionsInline ? 'container-fluid gridPanelModern' : 'container-fluid'}>
      {gridTitle && (
        <div className="row gridPanelTitleRow">
          <div className="col-md-12 pl-0 gridPanelTitle">
            {isActionsInline && goBack ? (
              <div className="gridModernTitleBlock">
                <button type="button" className="gridModernBack" onClick={goBack}>
                  <span aria-hidden="true">←</span>
                  {goBackLabel}
                </button>
                <div>
                  {gridEyebrow ? <span className="gridModernEyebrow">{gridEyebrow}</span> : null}
                  <div className="gridModernTitleLine">
                    <h1>{gridTitle}</h1>
                    {typeof gridTitleCount === 'number' && <span>{gridTitleCount}</span>}
                  </div>
                  {gridSubtitle ? <p>{gridSubtitle}</p> : null}
                </div>
              </div>
            ) : (
              <>
                {goBack && <Button text={'← back'} onClick={goBack} customClass={'btn-secondary mr-3'} />}
                {gridTitleContent || <h1>{gridTitle}</h1>}
                {!gridTitleContent && typeof gridTitleCount === 'number' && <span>{gridTitleCount}</span>}
              </>
            )}
            {isActionsInline && toolbarSearchField && (
              <label className="gridModernSearch">
                <input
                  value={toolbarSearchDraft}
                  placeholder={toolbarSearchPlaceholder || 'Search…'}
                  onChange={event => setToolbarSearchDraft(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && onApplyFilterClick && onTableFilterChange) {
                      event.preventDefault();
                      onTableFilterChange({ target: { value: toolbarSearchDraft } }, toolbarSearchField);
                      window.setTimeout(onApplyFilterClick, 0);
                    }
                  }}
                />
                {toolbarSearchDraft && onToolbarSearchClear ? (
                  <button
                    type="button"
                    className="gridModernSearch__clear"
                    onClick={() => {
                      setToolbarSearchDraft('');
                      onToolbarSearchClear();
                    }}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                ) : null}
              </label>
            )}
            {isActionsInline && isShowFilters && (
              <div className="gridModernFiltersAnchor" ref={filtersRef}>
                <button
                  type="button"
                  className={`gridModernFilterButton ${isShowFilter || isDataFiltered ? 'is-active' : ''}`}
                  onClick={onTableFilterClick}
                >
                  <span aria-hidden="true" />
                  Filters
                  {activeFilters.length > 0 && <b>{activeFilters.length}</b>}
                </button>
                {isShowFilter && (
                  <div
                    className="gridModernFilterPopover"
                    onKeyDown={event => {
                      const target = event.target as HTMLElement;
                      if (event.key === 'Enter' && (target.tagName === 'INPUT' || target.tagName === 'SELECT')) {
                        event.preventDefault();
                        applyFilters();
                      }
                    }}
                  >
                    <header>
                      <strong>Filters</strong>
                      <button type="button" onClick={closeFilters} aria-label="Close filters">
                        ×
                      </button>
                    </header>
                    <div className="gridModernFilterPopover__fields">
                      {columns
                        .filter(
                          column =>
                            column.type !== FILTER_TYPE.NONE &&
                            !hiddenFilterFields.includes(column.field) &&
                            column.field !== toolbarSearchField
                        )
                        .map(column => {
                          if (column.type === FILTER_TYPE.TEXT) {
                            return (
                              <label key={column.field}>
                                <span>{column.text}</span>
                                <input
                                  value={filters[column.field] || ''}
                                  onChange={event => onTableFilterChange && onTableFilterChange(event, column.field)}
                                />
                              </label>
                            );
                          }
                          if (column.type === FILTER_TYPE.DATE_INTERVAL) {
                            const value = filters[column.field] || {};
                            return (
                              <SitDatePicker
                                key={column.field}
                                label={`${column.text} interval`}
                                mode="range"
                                value={{
                                  start: value.from ? moment(value.from, 'DD-MM-YYYY').toDate() : null,
                                  end: value.to ? moment(value.to, 'DD-MM-YYYY').toDate() : null
                                }}
                                onChange={(range: any) => {
                                  const nextRange = range || {};
                                  if (onTableFilterChange) {
                                    onTableFilterChange(
                                      {
                                        target: {
                                          value: nextRange.start ? moment(nextRange.start).format('DD-MM-YYYY') : ''
                                        }
                                      },
                                      column.field,
                                      DATE_INTERVAL.FROM
                                    );
                                    window.setTimeout(
                                      () =>
                                        onTableFilterChange!(
                                          {
                                            target: {
                                              value: nextRange.end ? moment(nextRange.end).format('DD-MM-YYYY') : ''
                                            }
                                          },
                                          column.field,
                                          DATE_INTERVAL.TO
                                        ),
                                      0
                                    );
                                  }
                                }}
                                format="DD-MM-YYYY"
                                clearable={true}
                                syncRangeOnSelect={true}
                              />
                            );
                          }
                          if (column.type === FILTER_TYPE.DATETIME_INTERVAL) {
                            const value = filters[column.field] || {};
                            return (
                              <fieldset key={column.field} className="gridModernFilterInterval">
                                <legend>{column.text} interval</legend>
                                <input
                                  type="datetime-local"
                                  step={60}
                                  value={value.from || ''}
                                  onChange={event =>
                                    onTableFilterChange && onTableFilterChange(event, column.field, DATE_INTERVAL.FROM)
                                  }
                                  aria-label={`${column.text} from`}
                                />
                                <input
                                  type="datetime-local"
                                  step={60}
                                  value={value.to || ''}
                                  onChange={event =>
                                    onTableFilterChange && onTableFilterChange(event, column.field, DATE_INTERVAL.TO)
                                  }
                                  aria-label={`${column.text} to`}
                                />
                              </fieldset>
                            );
                          }
                          if (column.type === FILTER_TYPE.TIME_INTERVAL) {
                            const value = filters[column.field] || {};
                            const parseTime = (time?: string): Date | null => {
                              if (!time) return null;
                              const [hours, minutes] = time.split(':').map(Number);
                              const date = new Date();
                              date.setHours(hours, minutes, 0, 0);
                              return date;
                            };
                            const changeTime = (time: Date | null, intervalSide: string) => {
                              if (!onTableFilterChange) return;
                              onTableFilterChange(
                                { target: { value: time ? moment(time).format('HH:mm') : '' } },
                                column.field,
                                intervalSide
                              );
                            };
                            return (
                              <fieldset key={column.field} className="gridModernFilterInterval">
                                <legend>{column.text} interval</legend>
                                <TimePicker
                                  value={parseTime(value.from)}
                                  onChange={time => changeTime(time, DATE_INTERVAL.FROM)}
                                  placeholder="From"
                                  minuteStep={5}
                                  clearable={true}
                                  showConfirm={true}
                                  size="sm"
                                />
                                <TimePicker
                                  value={parseTime(value.to)}
                                  onChange={time => changeTime(time, DATE_INTERVAL.TO)}
                                  placeholder="To"
                                  minuteStep={5}
                                  clearable={true}
                                  showConfirm={true}
                                  size="sm"
                                  align="right"
                                />
                              </fieldset>
                            );
                          }
                          if (column.type === FILTER_TYPE.MULTISELECT) {
                            const selectedValues = Array.isArray(filters[column.field])
                              ? filters[column.field].map(String)
                              : filters[column.field]
                              ? [String(filters[column.field])]
                              : [];
                            const isRegistrationFormFilter =
                              ['permissionFormIds', 'userFormName'].includes(column.field) ||
                              (column.field === 'forms' && !/year group|age group/i.test(String(column.text)));
                            if (isRegistrationFormFilter) {
                              return (
                                <SearchMultiSelectFilter
                                  key={column.field}
                                  label={String(column.text || 'Registration forms')}
                                  selected={selectedValues}
                                  options={(options[column.field] || []).map(option => ({
                                    value: String(option.value),
                                    label: String(option.text || option.value)
                                  }))}
                                  onToggle={optionValue => {
                                    if (!onTableFilterChange) return;
                                    const isSelected = selectedValues.includes(optionValue);
                                    const nextValues = isSelected
                                      ? selectedValues.filter(value => value !== optionValue)
                                      : [...selectedValues, optionValue];
                                    onTableFilterChange(
                                      {
                                        target: {
                                          value: nextValues,
                                          options: (options[column.field] || []).map(candidate => ({
                                            value: String(candidate.value),
                                            selected: nextValues.includes(String(candidate.value))
                                          }))
                                        }
                                      },
                                      column.field
                                    );
                                  }}
                                  placeholder="Search registration forms…"
                                />
                              );
                            }
                            const isSportFilter = ['sport', 'sportId', 'sportIds', 'sports', 'eventSport'].includes(column.field);
                            if (isSportFilter) {
                              return (
                                <SearchMultiSelectFilter
                                  key={column.field}
                                  label={String(column.text || 'Sports')}
                                  selected={selectedValues}
                                  options={(options[column.field] || []).map(option => ({
                                    value: String(option.value),
                                    label: String(option.text || option.value)
                                  }))}
                                  onToggle={optionValue => {
                                    if (!onTableFilterChange) return;
                                    const isSelected = selectedValues.includes(optionValue);
                                    const nextValues = isSelected
                                      ? selectedValues.filter(value => value !== optionValue)
                                      : [...selectedValues, optionValue];
                                    onTableFilterChange(
                                      {
                                        target: {
                                          value: nextValues,
                                          options: (options[column.field] || []).map(candidate => ({
                                            value: String(candidate.value),
                                            selected: nextValues.includes(String(candidate.value))
                                          }))
                                        }
                                      },
                                      column.field
                                    );
                                  }}
                                  placeholder="Search sports…"
                                  emptyText="No matching sports"
                                  showOptionsOnFocus={column.field === 'eventSport'}
                                />
                              );
                            }
                            const isHouseFilter = [
                              'houses',
                              'studentHouseName',
                              'permissionHouseIds',
                              'houseId'
                            ].includes(column.field);
                            const isUserTagFilter = column.field === 'tag';
                            if (isHouseFilter || isUserTagFilter) {
                              return (
                                <FilterChipSelect
                                  key={column.field}
                                  label={String(column.text || (isHouseFilter ? 'Houses' : 'Tags'))}
                                  selected={selectedValues}
                                  collapsedLimit={6}
                                  options={(options[column.field] || []).map(option => ({
                                    value: String(option.value),
                                    label: String(option.text || option.value),
                                    color: isUserTagFilter ? option.color : undefined
                                  }))}
                                  onToggle={optionValue => {
                                    if (!onTableFilterChange) return;
                                    const isSelected = selectedValues.includes(optionValue);
                                    const nextValues = isSelected
                                      ? selectedValues.filter(value => value !== optionValue)
                                      : [...selectedValues, optionValue];
                                    onTableFilterChange(
                                      {
                                        target: {
                                          value: nextValues,
                                          options: (options[column.field] || []).map(candidate => ({
                                            value: String(candidate.value),
                                            selected: nextValues.includes(String(candidate.value))
                                          }))
                                        }
                                      },
                                      column.field
                                    );
                                  }}
                                />
                              );
                            }
                            if (column.filterControl === 'select') {
                              return (
                                <label key={column.field}>
                                  <span>{column.text}</span>
                                  <select
                                    value={selectedValues[0] || ''}
                                    onChange={event => {
                                      if (!onTableFilterChange) return;
                                      onTableFilterChange(
                                        {
                                          target: {
                                            value: event.target.value ? [event.target.value] : [],
                                            options: (options[column.field] || []).map(option => ({
                                              value: String(option.value),
                                              selected: String(option.value) === String(event.target.value)
                                            }))
                                          }
                                        },
                                        column.field
                                      );
                                    }}
                                  >
                                    <option value="">All {String(column.text).toLowerCase()}</option>
                                    {(options[column.field] || []).map(option => (
                                      <option key={String(option.value)} value={option.value}>
                                        {option.text}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              );
                            }
                            return (
                              <fieldset key={column.field} className="gridModernFilterChips">
                                <legend>{column.text}</legend>
                                <div>
                                  {(options[column.field] || []).map(option => {
                                    const optionValue = String(option.value);
                                    const isSelected = selectedValues.includes(optionValue);
                                    return (
                                      <button
                                        key={optionValue}
                                        type="button"
                                        className={`${isSelected ? 'is-selected' : ''} ${
                                          option.color ? 'has-option-color' : ''
                                        }`.trim()}
                                        style={
                                          option.color
                                            ? ({ '--grid-filter-option-color': option.color } as React.CSSProperties)
                                            : undefined
                                        }
                                        aria-pressed={isSelected}
                                        onClick={() => {
                                          if (!onTableFilterChange) return;
                                          const nextValues = isSelected
                                            ? selectedValues.filter(value => value !== optionValue)
                                            : [...selectedValues, optionValue];
                                          onTableFilterChange(
                                            {
                                              target: {
                                                value: nextValues,
                                                options: (options[column.field] || []).map(candidate => ({
                                                  value: String(candidate.value),
                                                  selected: nextValues.includes(String(candidate.value))
                                                }))
                                              }
                                            },
                                            column.field
                                          );
                                        }}
                                      >
                                        {option.text}
                                      </button>
                                    );
                                  })}
                                </div>
                              </fieldset>
                            );
                          }
                          if (column.type === FILTER_TYPE.SELECT) {
                            return (
                              <label key={column.field}>
                                <span>{column.text}</span>
                                <select
                                  value={filters[column.field] || ''}
                                  onChange={event => onTableFilterChange && onTableFilterChange(event, column.field)}
                                >
                                  <option value="">All</option>
                                  {(options[column.field] || []).map(option => (
                                    <option key={String(option.value)} value={option.value}>
                                      {option.text}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            );
                          }
                          return null;
                        })}
                    </div>
                    <footer>
                      <button type="button" className="is-clear" onClick={clearFilters}>
                        Clear all
                      </button>
                      <div>
                        <button type="button" onClick={closeFilters}>
                          Cancel
                        </button>
                        <button type="button" className="is-apply" onClick={applyFilters}>
                          Apply
                        </button>
                      </div>
                    </footer>
                  </div>
                )}
              </div>
            )}
            {isActionsInline && allColumns && allColumns.length > 1 && (
              <div className="gridModernColumns" ref={columnsRef}>
                <button type="button" onClick={() => setIsColumnsOpen(!isColumnsOpen)}>
                  <span aria-hidden="true" />
                  Columns
                  <b>{allColumns.filter(column => !hiddenColumns.includes(column.field)).length}</b>
                </button>
                {isColumnsOpen && (
                  <div className="gridModernColumns__menu">
                    <header>
                      <strong>Show / hide columns</strong>
                      <button type="button" onClick={() => onResetColumns && onResetColumns()}>
                        Reset
                      </button>
                    </header>
                    {allColumns.map(column => (
                      <label key={column.field}>
                        <input
                          type="checkbox"
                          checked={!hiddenColumns.includes(column.field)}
                          disabled={fixedColumnFields.includes(column.field)}
                          onChange={() => onToggleColumn && onToggleColumn(column.field)}
                        />
                        {column.text}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            {isActionsInline && isRenderAdditionalOptionsExist && (
              <div className="gridModernAdditionalOptions">{renderAdditionalOptions()}</div>
            )}
            {isActionsInline && actionItems.length > 0 && (!selectionActionsBelow || selectedItems.length === 0) && (
              <div className="gridInlineActions">
                {actionItems
                  .filter(item => item.isActive)
                  .map(item => (
                    <button
                      key={item.itemText}
                      type="button"
                      className={item.isDanger ? 'is-danger' : ''}
                      onClick={item.onItemClick}
                    >
                      {item.itemText}
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="row">
        <div className="col-md-6 pl-0 mb-sm-0 mb-3">
          {!isActionsInline && actionItems.length > 0 && <ActionsButton actionItems={actionItems} />}
        </div>
        <div className="col-md-6 pl-0">
          {!isActionsInline && (
            <Pagination2 currentPage={currentPage} setCurrentPageParams={setCurrentPageParams} total={total} />
          )}
        </div>
      </div>
      <div className="row">
        <div className="col-md-12 pl-0">
          {!isActionsInline && (
            <SelectAllCheckbox
              onSelectAllClick={onSelectAllClick}
              onSelectAllOnPageClick={onSelectAllOnPageClick}
              onUnselectAllClick={onUnselectAllClick}
              isChecked={isSelectAllChecked}
              totalCount={total}
              currentPage={currentPage}
              selectedItems={selectedItems}
              selectedItemsCount={selectedItemsCount}
              isSelectDisabled={isSelectDisabled}
            />
          )}

          <div className="d-flex align-items-center flex-wrap gap-1 mt-2">
            {!isActionsInline && isShowFilters && (
              <FilterIcon
                onTableFilterClick={onTableFilterClick}
                isFilterShown={isShowFilter}
                isDataFiltered={isDataFiltered}
              />
            )}
            {isPDF && <PdfIcon selectedItems={selectedItems} />}
            {isCSV && <CsvIcon selectedItems={selectedItems} columns={columns} />}
            {!isActionsInline && isRenderAdditionalOptionsExist && <div>{renderAdditionalOptions()}</div>}
          </div>
        </div>
      </div>
      {(activeFilters.length > 0 || isToolbarSearchActive) && (
        <div className="gridActiveFilters">
          {isToolbarSearchActive && (
            <span>
              <b>Search:</b> {filters[toolbarSearchField!]}
              {onClearIndividualFilter && (
                <button
                  type="button"
                  onClick={() => onClearIndividualFilter(toolbarSearchField!)}
                  aria-label="Remove search filter"
                >
                  ×
                </button>
              )}
            </span>
          )}
          {activeFilters.map(column => (
            <span key={column.field}>
              <b>{column.text}:</b> {getActiveFilterLabel(column)}
              {onClearIndividualFilter && (
                <button
                  type="button"
                  onClick={() => onClearIndividualFilter(column.field)}
                  aria-label={`Remove ${column.text} filter`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {onClearFilterClick && (
            <button type="button" onClick={onClearFilterClick}>
              Clear all
            </button>
          )}
        </div>
      )}
      {isActionsInline && selectionActionsBelow && (selectedItemsCount ?? selectedItems.length) > 0 ? (
        <div className="gridTableActions" aria-label="Selected record actions">
          <span className="gridTableActions__selection">
            <strong>{selectedItemsCount ?? selectedItems.length}</strong> selected
            {showClearSelection && (
              <button type="button" className="gridTableActions__clear" onClick={onUnselectAllClick}>
                Clear selection
              </button>
            )}
          </span>
          <div className="gridTableActions__buttons">
            {actionItems
              .filter(item => item.isActive)
              .map(item => (
                <button
                  key={item.itemText}
                  type="button"
                  className={item.isDanger ? 'is-danger' : ''}
                  onClick={item.onItemClick}
                >
                  {item.itemText}
                </button>
              ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

GridPanel.displayName = 'GridPanel';
